import {
  DialogTitle,
  Button,
  Box,
  Typography,
  IconButton,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import {
  Close,
  FileCopy,
  GetApp,
  PictureAsPdf,
  ArrowDropDown,
  CropOriginal,
} from "@material-ui/icons";
import React, { useState } from "react";
import { PopupDialog } from "../../../../components/common";
import { Alert } from "@material-ui/lab";

const QrCodeModal = ({ selectedMediaFile, onClose }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);

  // Function to get the QR code image URL
  const getQrCodeUrl = () => {
    if (!selectedMediaFile?.qrcode) return "";

    if (selectedMediaFile.qrcode.startsWith("data:")) {
      return selectedMediaFile.qrcode;
    }

    if (selectedMediaFile.qrcode.startsWith("iVBORw0KGgo")) {
      return `data:image/png;base64,${selectedMediaFile.qrcode}`;
    }

    return selectedMediaFile.qrcode;
  };

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // 1. PNG Download
  const handleDownloadPNG = () => {
    const qrCodeUrl = getQrCodeUrl();
    if (!qrCodeUrl) return;

    try {
      const link = document.createElement("a");
      link.href = qrCodeUrl;
      const fileName = selectedMediaFile.name
        ? selectedMediaFile.name.replace(/\.[^/.]+$/, "") // Remove extension
        : "qrcode";
      link.download = `qrcode-${fileName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbarMessage("QR code downloaded as PNG!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error downloading QR code:", error);
      setSnackbarMessage("Failed to download QR code");
      setSnackbarOpen(true);
    }
    handleMenuClose();
  };

  // 2. SVG Download
  const handleDownloadSVG = () => {
    const qrCodeUrl = getQrCodeUrl();
    if (!qrCodeUrl) {
      handleDownloadPNG();
      return;
    }

    try {
      const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg width="300" height="300" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <title>QR Code</title>
  <image xlink:href="${qrCodeUrl}" x="0" y="0" width="300" height="300" />
</svg>`;

      const blob = new Blob([svgContent], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName = selectedMediaFile.name
        ? selectedMediaFile.name.replace(/\.[^/.]+$/, "")
        : "qrcode";
      link.download = `qrcode-${fileName}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbarMessage("QR code downloaded as SVG!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error creating SVG:", error);
      handleDownloadPNG();
    }
    handleMenuClose();
  };

  // 3. PDF Download
  const handleDownloadPDF = () => {
    const qrCodeUrl = getQrCodeUrl();
    if (!qrCodeUrl) return;

    const windowName = `qr_pdf_${Date.now()}`;
    const printWindow = window.open(
      "",
      windowName,
      "width=800,height=600,scrollbars=yes"
    );

    if (!printWindow) {
      setSnackbarMessage("Please allow pop-ups for this site to download PDF");
      setSnackbarOpen(true);
      handleMenuClose();
      return;
    }

    const htmlContent = `
      <html>
        <head>
          <title>QR Code</title>
          <style>
            @media print {
              @page {
                size: A4 portrait;
                margin: 20mm;
              }
              
              body {
                font-family: Arial, sans-serif;
                text-align: center;
                padding: 0;
                margin: 0;
              }
            }
            
            body {
              font-family: Arial, sans-serif;
              text-align: center;
              padding: 40px;
              max-width: 210mm;
              margin: 0 auto;
            }
            
            .header {
              margin-bottom: 30px;
            }
            
            .qr-container {
              margin: 30px auto;
              padding: 20px;
              display: inline-block;
            }
            
            .qr-image {
              width: 200px;
              height: 200px;
              border: 1px solid #ccc;
              padding: 10px;
            }
            
            .footer {
              margin-top: 40px;
              color: #666;
              font-size: 12px;
            }
            
            h1 {
              color: #333;
              margin-bottom: 10px;
            }
            
            .no-print {
              display: none;
            }
            
            @media screen {
              .print-instructions {
                background: #fff3cd;
                border: 1px solid #ffeaa7;
                padding: 10px;
                border-radius: 4px;
                margin: 20px 0;
                color: #856404;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>QR Code</h1>
            ${
              selectedMediaFile.name
                ? `<p><strong>File:</strong> ${selectedMediaFile.name}</p>`
                : ""
            }
          </div>
          
          <div class="qr-container">
            <img src="${qrCodeUrl}" alt="QR Code" class="qr-image" />
          </div>
          
          <div class="footer">
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="print-instructions no-print">
            <p><strong>Instructions:</strong> Use your browser's print dialog (Ctrl+P) and choose "Save as PDF" as the destination.</p>
            <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
              Open Print Dialog
            </button>
            <button onclick="window.close()" style="padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">
              Close Window
            </button>
          </div>
          
          <script>
            window.opener = null;
            window.onbeforeunload = null;
            
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
            
            window.onafterprint = function() {
              setTimeout(function() {
                window.close();
              }, 100);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    window.focus();

    setSnackbarMessage("Opening PDF print dialog...");
    setSnackbarOpen(true);
    handleMenuClose();
  };

  // Copy URL function
  const handleCopyUrl = async (url, type = "URL") => {
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setSnackbarMessage(`${type} copied to clipboard!`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error(`Error copying ${type}:`, error);
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      setSnackbarMessage(`${type} copied to clipboard!`);
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const qrCodeUrl = getQrCodeUrl();
  const hasQrCode = !!qrCodeUrl;
  const hasShortUrl = !!selectedMediaFile?.shortnerUrl;
  const hasLongUrl = !!selectedMediaFile?.url;

  return (
    <>
      <PopupDialog
        maxWidth={true}
        visible={selectedMediaFile}
        onClose={onClose}>
        <DialogTitle id='alert-dialog-title'>
          {"QR Code"}
          <Close className='closePopup' onClick={onClose} />
        </DialogTitle>

        <div style={{ padding: "20px", maxWidth: "500px", margin: "0 auto" }}>
          {hasQrCode ? (
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              mb={3}>
              <Typography variant='h6' gutterBottom>
                QR Code
              </Typography>

              {/* QR Code Image */}
              <div>
                <img
                  src={qrCodeUrl}
                  alt='QR Code'
                  style={{
                    width: "300px",
                    height: "300px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "10px",
                    backgroundColor: "white",
                  }}
                  onError={e => {
                    console.error("Error loading QR code image");
                    e.target.style.display = "none";
                  }}
                />
              </div>

              {/* Download Button with Menu */}
              <Box
                mt={2}
                display='flex'
                flexDirection='column'
                alignItems='center'>
                <Button
                  variant='contained'
                  color='primary'
                  endIcon={<ArrowDropDown />}
                  onClick={handleMenuOpen}
                  style={{ marginTop: "16px", minWidth: "200px" }}>
                  Download QR Code
                </Button>

                <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}>
                  <MenuItem onClick={handleDownloadPNG}>
                    <ListItemIcon>
                      <GetApp fontSize='small' />
                    </ListItemIcon>
                    <ListItemText primary='Download as PNG' />
                  </MenuItem>
                  <MenuItem onClick={handleDownloadPDF}>
                    <ListItemIcon>
                      <PictureAsPdf fontSize='small' />
                    </ListItemIcon>
                    <ListItemText primary='Download as PDF' />
                  </MenuItem>
                  <MenuItem onClick={handleDownloadSVG}>
                    <ListItemIcon>
                      <CropOriginal fontSize='small' />
                    </ListItemIcon>
                    <ListItemText primary='Download as SVG' />
                  </MenuItem>
                </Menu>

                <Typography
                  variant='caption'
                  color='textSecondary'
                  style={{ marginTop: "8px" }}>
                  Choose format: PNG, PDF, or SVG
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              mb={3}
              py={4}>
              <Typography variant='h6' gutterBottom color='textSecondary'>
                QR Code Not Available
              </Typography>
              <Typography variant='body1' color='textSecondary' align='center'>
                No QR code image available for this item
              </Typography>
            </Box>
          )}

          {/* URL Information Section - Clean and Simple */}
          {(hasShortUrl || hasLongUrl) && (
            <Box mt={3}>
              <Typography variant='subtitle1' gutterBottom>
                URL Information
              </Typography>

              {hasShortUrl && (
                <Box mb={2}>
                  <Typography variant='body2' gutterBottom>
                    Short URL
                  </Typography>
                  <Box display='flex' alignItems='center'>
                    <Typography
                      variant='body1'
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                        marginRight: "8px",
                        wordBreak: "break-all",
                      }}>
                      {selectedMediaFile.shortnerUrl}
                    </Typography>
                    <IconButton
                      color='primary'
                      onClick={() =>
                        handleCopyUrl(
                          selectedMediaFile.shortnerUrl,
                          "Short URL"
                        )
                      }
                      title='Copy Short URL'>
                      <FileCopy />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {hasLongUrl && (
                <Box mb={1}>
                  <Typography variant='body2' gutterBottom>
                    Original URL
                  </Typography>
                  <Box display='flex' alignItems='center'>
                    <Typography
                      variant='body1'
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                        marginRight: "8px",
                        wordBreak: "break-all",
                        fontSize: "0.9rem",
                      }}>
                      {selectedMediaFile.url}
                    </Typography>
                    <IconButton
                      color='primary'
                      onClick={() =>
                        handleCopyUrl(selectedMediaFile.url, "Long URL")
                      }
                      title='Copy Long URL'>
                      <FileCopy />
                    </IconButton>
                  </Box>
                </Box>
              )}

              <Typography variant='caption' color='textSecondary'>
                Click the copy button to copy the URL to clipboard
              </Typography>
            </Box>
          )}

          {!hasQrCode && !hasShortUrl && !hasLongUrl && (
            <Box textAlign='center' py={4}>
              <Typography variant='body1' color='textSecondary'>
                No QR code or URL data available
              </Typography>
            </Box>
          )}
        </div>
      </PopupDialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={handleSnackbarClose} severity='success'>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export { QrCodeModal };
